import { useMutation, useQuery } from "@tanstack/react-query";
import { ShieldCheckIcon, SmartphoneIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { encode } from "uqr";
import { Field } from "@/components/app/field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { errorMessage } from "@/lib/errors";
import { useTRPC } from "@/lib/trpc";
import { FormError } from "./sign-in";

/**
 * Two-factor sign-in (ADR 0011): after the password, the code an authenticator app shows (TOTP),
 * or a backup code when the phone is away. Any authenticator app works; turning it on shows a QR
 * code, the secret to type, and the backup codes once.
 */

/** The code field, shown once the server asks for it (`TWO_FACTOR_REQUIRED`). */
export function SecondFactorField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Field label={t("twoFactor.code")} htmlFor={id} hint={t("twoFactor.codeHint")}>
      <Input
        id={id}
        autoFocus
        inputMode="numeric"
        autoComplete="one-time-code"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/** A QR code as SVG, drawn from its modules: nothing from elsewhere, nothing inline to run. */
function QrCode({ text, label }: { text: string; label: string }) {
  const { data, size } = encode(text, { border: 2 });
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label}
      className="size-48 rounded-lg bg-white p-1"
      shapeRendering="crispEdges"
    >
      <title>{label}</title>
      {data.flatMap((row, y) =>
        row.map((dark, x) =>
          dark ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: a module's place is its identity
            <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#000" />
          ) : null,
        ),
      )}
    </svg>
  );
}

/** Backup codes, shown once, with the promise to keep them before going on. */
function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation();
  const [kept, setKept] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">{t("twoFactor.backupBody")}</p>
      <ul
        className="grid grid-cols-2 gap-2 rounded-lg bg-muted px-4 py-3 font-mono text-sm"
        data-testid="backup-codes"
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <Button
        type="button"
        variant="outline"
        className="self-start"
        onClick={() =>
          navigator.clipboard
            .writeText(codes.join("\n"))
            .then(() => toast.success(t("keys.copied")))
            .catch(() => toast.error(t("keys.copyFailed")))
        }
      >
        {t("keys.copy")}
      </Button>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-4 accent-brand"
          checked={kept}
          onChange={(e) => setKept(e.target.checked)}
        />
        {t("keys.kept")}
      </label>
      <Button disabled={!kept} onClick={onDone}>
        {t("keys.done")}
      </Button>
    </div>
  );
}

type Step =
  | { kind: "password"; action: "enable" | "disable" | "codes" }
  | { kind: "scan"; uri: string; backupCodes: string[] }
  | { kind: "codes"; backupCodes: string[] };

/** Profile: two-factor sign-in, turned on, off, or given new backup codes. */
export function TwoFactorCard() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { data: status, refetch } = useQuery(trpc.account.twoFactor.status.queryOptions());
  const enable = useMutation({
    ...trpc.account.twoFactor.enable.mutationOptions(),
    meta: { silent: true },
  });
  const confirm = useMutation({
    ...trpc.account.twoFactor.confirm.mutationOptions(),
    meta: { silent: true },
  });
  const disable = useMutation({
    ...trpc.account.twoFactor.disable.mutationOptions(),
    meta: { silent: true },
  });
  const newCodes = useMutation({
    ...trpc.account.twoFactor.newBackupCodes.mutationOptions(),
    meta: { silent: true },
  });
  const [step, setStep] = useState<Step | null>(null);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pending = enable.isPending || confirm.isPending || disable.isPending || newCodes.isPending;

  const open = (next: Step) => {
    setPassword("");
    setCode("");
    setError(null);
    setStep(next);
  };
  const close = () => {
    setStep(null);
    void refetch();
  };
  const attempt = async (work: () => Promise<void>) => {
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(errorMessage(e, t));
    }
  };

  if (!status) return null;
  const secret = step?.kind === "scan" ? (new URL(step.uri).searchParams.get("secret") ?? "") : "";
  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <p className="flex min-w-0 flex-1 items-start gap-2 text-sm text-muted-foreground">
          {status.enabled ? (
            <ShieldCheckIcon className="mt-0.5 size-4 shrink-0 text-ok" />
          ) : (
            <SmartphoneIcon className="mt-0.5 size-4 shrink-0" />
          )}
          {status.enabled ? t("twoFactor.on") : t("twoFactor.off")}
        </p>
        {status.enabled ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => open({ kind: "password", action: "codes" })}>
              {t("twoFactor.newCodes")}
            </Button>
            <Button variant="outline" onClick={() => open({ kind: "password", action: "disable" })}>
              {t("twoFactor.turnOff")}
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            className="self-start sm:self-auto"
            onClick={() => open({ kind: "password", action: "enable" })}
          >
            {t("twoFactor.turnOn")}
          </Button>
        )}
      </div>
      <Dialog open={step !== null} onOpenChange={(next) => (next ? null : close())}>
        <DialogContent>
          <DialogHeader
            title={t("twoFactor.title")}
            description={
              step?.kind === "scan"
                ? t("twoFactor.scanBody")
                : step?.kind === "codes"
                  ? t("twoFactor.backupTitle")
                  : t("twoFactor.passwordBody")
            }
          />
          {step?.kind === "password" ? (
            <form
              className="contents"
              onSubmit={(event) => {
                event.preventDefault();
                void attempt(async () => {
                  if (step.action === "enable") {
                    const result = await enable.mutateAsync({ password });
                    setStep({ kind: "scan", uri: result.uri, backupCodes: result.backupCodes });
                  } else if (step.action === "codes") {
                    const result = await newCodes.mutateAsync({ password });
                    setStep({ kind: "codes", backupCodes: result.backupCodes });
                  } else {
                    await disable.mutateAsync({ password });
                    toast.success(t("twoFactor.turnedOff"));
                    close();
                  }
                });
              }}
            >
              <DialogBody className="flex flex-col gap-4">
                <Field label={t("account.deletePassword")} htmlFor="two-factor-password">
                  <Input
                    id="two-factor-password"
                    type="password"
                    autoFocus
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
                <FormError message={error} />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close}>
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant={step.action === "disable" ? "destructive" : "default"}
                  disabled={pending || !password}
                >
                  {step.action === "disable" ? t("twoFactor.turnOff") : t("welcome.continue")}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
          {step?.kind === "scan" ? (
            <form
              className="contents"
              onSubmit={(event) => {
                event.preventDefault();
                void attempt(async () => {
                  await confirm.mutateAsync({ code });
                  toast.success(t("twoFactor.turnedOn"));
                  setStep({ kind: "codes", backupCodes: step.backupCodes });
                });
              }}
            >
              <DialogBody className="flex flex-col items-start gap-4">
                <QrCode text={step.uri} label={t("twoFactor.qrLabel")} />
                <p className="text-sm text-muted-foreground">
                  {t("twoFactor.secretHint")}{" "}
                  <span
                    className="font-mono break-all text-foreground select-all"
                    data-testid="totp-secret"
                  >
                    {secret}
                  </span>
                </p>
                <a href={step.uri} className="text-sm text-brand hover:underline">
                  {t("twoFactor.openApp")}
                </a>
                <div className="w-full">
                  <SecondFactorField id="two-factor-confirm" value={code} onChange={setCode} />
                </div>
                <FormError message={error} />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={pending || !code}>
                  {t("twoFactor.confirm")}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
          {step?.kind === "codes" ? (
            <DialogBody>
              <BackupCodes codes={step.backupCodes} onDone={close} />
            </DialogBody>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
